import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, AppstoreOutlined, HomeOutlined, UserOutlined } from "@ant-design/icons";

/**
 * Мобайл апп бүрхүүл — native (Capacitor) дээр л ашиглагдана.
 *
 * Асуудал: мобайл горимд Header/Breadcrumb/Tabs бүгд нуугддаг тул дэлгэц дээр
 * НАВИГАЦИ ОГТ ҮЛДЭХГҮЙ — зүгээр л вэб хуудас мэт харагддаг байв.
 *
 * Шийдэл: гар утасны аппын стандарт 2 элемент:
 *   • Дээд AppBar — гарчиг + буцах товч
 *   • Доод таб самбар — үндсэн навигаци
 *
 * ⚠ Вэб хувилбарт огт нөлөөлөхгүй — ProtectedRoute нь зөвхөн isMobile үед энэ
 * бүрхүүлийг сонгоно.
 */

// iOS-ийн стандарт навигацийн мөр 44pt (өмнө 52 байсан нь арай өндөр байв).
export const APP_BAR_HEIGHT = 44;
export const TAB_BAR_HEIGHT = 56;

/**
 * AppBar-ийн БҮТЭН өндөр = мөрийн өндөр + дэлгэцийн дээд аюулгүй бүс
 * (Dynamic Island / notch). Контентын paddingTop-д мөн энэ утгыг ашиглана.
 */
export const APP_BAR_TOTAL = `calc(${APP_BAR_HEIGHT}px + env(safe-area-inset-top))`;

const PRIMARY = "#0c4a93";

/** Доод самбарын табууд. Замын угтвараар идэвхтэйг тодорхойлно. */
const TABS = [
    { key: "home", label: "Нүүр", to: "/dashboard", icon: HomeOutlined, match: ["/dashboard", "/"] },
    { key: "flows", label: "Урсгал", to: "/si", icon: AppstoreOutlined, match: ["/si", "/pf"] },
    { key: "profile", label: "Профайл", to: "/profile", icon: UserOutlined, match: ["/profile"] },
];

/** Таб бүрийн үндсэн зам — эдгээр дээр байхад "буцах" товч харуулахгүй. */
const ROOT_PATHS = ["/", "/dashboard", "/si", "/profile"];

const titleFor = (pathname) => {
    if (pathname === "/" || pathname.startsWith("/dashboard")) return "Нүүр";
    if (pathname.startsWith("/si")) return "Урсгал";
    if (pathname.startsWith("/pf")) return "Тохиргоо";
    if (pathname.startsWith("/rp")) return "Тайлан";
    if (pathname.startsWith("/profile")) return "Профайл";
    if (pathname.startsWith("/admin")) return "Удирдлага";
    if (pathname.startsWith("/global-admin")) return "Глобал удирдлага";
    return "MELP";
};

export const MobileAppBar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const canGoBack = !ROOT_PATHS.includes(location.pathname);

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                // Дээд аюулгүй бүсийг (Dynamic Island / notch) БҮРЭН бүрхэнэ.
                // Өмнө нь зөвхөн 52px өндөртэй байсан тул түүний ДЭЭД талын зурвас
                // дэвсгэргүй үлдэж, доогуур гүйлгэж буй контент нэвт харагддаг байв.
                height: APP_BAR_TOTAL,
                paddingTop: "env(safe-area-inset-top)",
                // ⚠ ЗААВАЛ border-box: эс бөгөөс `height` дээр `paddingTop` НЭМЭГДЭЖ,
                // аюулгүй бүс ДАВХАР тоологдоод мөр хэт өндөр болно
                // (44 + inset + inset ≈ 160pt болж байсан).
                boxSizing: "border-box",
                // ⚠ Бүрэн ТУНГАЛАГ БУС байх ёстой. Хагас тунгалаг + backdrop-blur нь
                // доорх контентыг нэвт харуулж, "дэвсгэргүй" мэт харагдуулж байсан.
                background: "#fff",
                boxShadow: "0 1px 0 rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                // ⚠ `padding` товчлолыг битгий ашигла — дээрх paddingTop-ыг дарна.
                paddingLeft: 8,
                paddingRight: 8,
                zIndex: 1000,
            }}
        >
            {/* Зүүн: буцах (эсвэл зайг тэнцвэржүүлэх хоосон блок) */}
            <div style={{ width: 44, flexShrink: 0 }}>
                {canGoBack && (
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        aria-label="Буцах"
                        style={{
                            width: 44,
                            height: 44,
                            border: "none",
                            background: "transparent",
                            fontSize: 18,
                            color: PRIMARY,
                            cursor: "pointer",
                        }}
                    >
                        <ArrowLeftOutlined />
                    </button>
                )}
            </div>

            <div
                style={{
                    flex: 1,
                    textAlign: "center",
                    fontSize: 17,
                    fontWeight: 600,
                    color: "#1a1a1a",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {titleFor(location.pathname)}
            </div>

            {/* Баруун: гарчгийг үнэхээр голлуулах тэнцвэрийн блок */}
            <div style={{ width: 44, flexShrink: 0 }} />
        </div>
    );
};

export const MobileTabBar = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const isActive = (tab) =>
        tab.match.some((m) => (m === "/" ? location.pathname === "/" : location.pathname.startsWith(m)));

    return (
        <div
            style={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                // Дээдтэй адил бүрэн тунгалаг бус — доогуур гүйлгэх контент нэвт харагдахгүй.
                background: "#fff",
                boxShadow: "0 -1px 0 rgba(0,0,0,0.06)",
                display: "flex",
                // Home indicator-тай төхөөрөмж дээр доод зайг нэмнэ (бусад дээр 0).
                // Энд `height` заагаагүй тул padding нь өндрийг зөв нэмнэ.
                paddingBottom: "env(safe-area-inset-bottom)",
                boxSizing: "border-box",
                zIndex: 1000,
            }}
        >
            {TABS.map((tab) => {
                const active = isActive(tab);
                const IconCmp = tab.icon;
                return (
                    <button
                        key={tab.key}
                        type="button"
                        onClick={() => navigate(tab.to)}
                        style={{
                            flex: 1,
                            height: TAB_BAR_HEIGHT,
                            border: "none",
                            background: "transparent",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2,
                            color: active ? PRIMARY : "#8c8c8c",
                            cursor: "pointer",
                        }}
                    >
                        <IconCmp style={{ fontSize: 20 }} />
                        <span style={{ fontSize: 11, fontWeight: active ? 600 : 400 }}>{tab.label}</span>
                    </button>
                );
            })}
        </div>
    );
};
